import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../lib/api';

export function useEngagementActions({ setState, refresh }) {
  const registerReferralOwner = useCallback((email, name) => {
    api.post('/referrals/register').catch(() => {});
  }, []);

  const updateReferralProgram = useCallback(async (cfg) => {
    try {
      await api.put('/admin/settings/referralProgram', { value: cfg });
      toast.success('Referral program saved', { description: 'Changes apply at checkout immediately.' });
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const createTicket = useCallback(async ({ subject, category, message }) => {
    try {
      await api.post('/tickets', { subject, category, message });
      toast.success('Ticket submitted', { description: 'KELATE support will reply here.' });
      await refresh(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const replyTicket = useCallback(async (ticketId, from, message, byAdmin) => {
    try {
      await api.post(`/tickets/${ticketId}/reply`, { message });
      await refresh(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const setTicketStatus = useCallback(async (ticketId, status) => {
    try {
      await api.post(`/admin/tickets/${ticketId}/status`, { status });
      toast.success(`Ticket ${status === 'RESOLVED' ? 'resolved' : status === 'OPEN' ? 'reopened' : 'updated'}`);
      await refresh(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const setRiderAvailability = useCallback((riderId, available) => {
    setState((s) => ({ ...s, riderAvailability: { ...s.riderAvailability, [riderId]: available } }));
    api.put('/rider/availability', { available }).catch(() => {});
  }, [setState]);

  const updateHomeHero = useCallback(async (cfg) => {
    try {
      await api.put('/admin/settings/homeHero', { value: cfg });
      toast.success('Homepage hero updated');
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const updateHomeLayout = useCallback(async (layout) => {
    try {
      await api.put('/admin/settings/homeLayout', { value: layout });
      toast.success('Homepage layout saved', { description: 'Changes are live on the homepage immediately.' });
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  return useMemo(() => ({
    registerReferralOwner, updateReferralProgram, createTicket, replyTicket, setTicketStatus,
    setRiderAvailability, updateHomeLayout, updateHomeHero,
  }), [registerReferralOwner, updateReferralProgram, createTicket, replyTicket, setTicketStatus,
    setRiderAvailability, updateHomeLayout, updateHomeHero]);
}
